/**
 * Web HTML5 drag-and-drop hooks for record table row reorder (#995 / #1154).
 */

import { useEffect } from 'react';
import { Platform, View } from 'react-native';

import type { RefObject } from 'react';

export function useWebDragHandle(
  ref: RefObject<View | null>,
  enabled: boolean,
  rowIndex: number,
  onDragStart: (index: number) => void,
  onDragEnd: () => void,
): void {
  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) {
      return undefined;
    }
    const node = ref.current as unknown as HTMLElement | null;
    if (!node) {
      return undefined;
    }

    node.draggable = true;
    node.style.cursor = 'grab';

    const handleDragStart = (event: DragEvent) => {
      event.stopPropagation();
      if (event.dataTransfer) {
        event.dataTransfer.setData('text/plain', String(rowIndex));
        event.dataTransfer.effectAllowed = 'move';
      }
      onDragStart(rowIndex);
    };
    const handleDragEnd = (event: DragEvent) => {
      event.stopPropagation();
      onDragEnd();
    };

    node.addEventListener('dragstart', handleDragStart);
    node.addEventListener('dragend', handleDragEnd);
    return () => {
      node.removeEventListener('dragstart', handleDragStart);
      node.removeEventListener('dragend', handleDragEnd);
      node.draggable = false;
      node.style.cursor = '';
    };
  }, [enabled, rowIndex, onDragStart, onDragEnd, ref]);
}

export function useWebDropTarget(
  ref: RefObject<View | null>,
  enabled: boolean,
  rowIndex: number,
  onDragOver: (index: number) => void,
  onDrop: (index: number) => void,
): void {
  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) {
      return undefined;
    }
    const node = ref.current as unknown as HTMLElement | null;
    if (!node) {
      return undefined;
    }

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      onDragOver(rowIndex);
    };
    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onDrop(rowIndex);
    };

    node.addEventListener('dragover', handleDragOver);
    node.addEventListener('drop', handleDrop);
    return () => {
      node.removeEventListener('dragover', handleDragOver);
      node.removeEventListener('drop', handleDrop);
    };
  }, [enabled, rowIndex, onDragOver, onDrop, ref]);
}
